import type { SchemaObject } from 'json-schema-traverse';
import traverse from 'json-schema-traverse';

import { stripSchemaSpecificPartsFromPath } from './getConditionalPaths';

/**
 * Returns the set of form-data paths whose value can drive a conditional
 * branch in the schema. A path is a "conditional driver" when its field key
 * appears in some `if.properties` block; paths are normalised to the same
 * shape as the values typed into form inputs (repeater indices collapsed
 * to `0`, schema-keyword segments stripped).
 *
 * A change at one of these paths must resolve the schema synchronously so
 * the conditional UI reveals immediately. Non-driver paths go through the
 * debounced refresh.
 */
export function getConditionalDriverFields(schema: SchemaObject | null | undefined): Set<string> {
  const drivers = new Set<string>();

  if (!schema) return drivers;

  traverse(schema, {
    cb: (currSchema, jsonPointer) => {
      const ifProps = currSchema?.if?.properties;
      const ifNotProps = currSchema?.if?.not?.properties;
      if (!ifProps && !ifNotProps) return;

      const dotPath = jsonPointer
        .split('/')
        .filter(Boolean)
        .map((segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~'))
        .join('.');

      const addFields = (props: Record<string, unknown>) => {
        Object.keys(props).forEach((field) => {
          const rawPath = dotPath ? `${dotPath}.${field}` : field;
          const normalised = stripSchemaSpecificPartsFromPath(rawPath);
          if (normalised) drivers.add(normalised);
        });
      };

      if (ifProps) addFields(ifProps);
      if (ifNotProps) addFields(ifNotProps);
    },
  });

  return drivers;
}

/**
 * Normalises a form-data path to the same shape stored in the driver set:
 * every numeric segment collapses to `0`. Used to match live runtime paths
 * (which carry actual repeater indices) against the static driver set.
 */
export function normaliseDataPathIndices(path: string): string {
  return path.replace(/\[(\d+)\]/g, '.$1').replace(/\.\d+(?=\.|$)/g, '.0');
}
